<template>
  <Section
    v-if="freightAllowance && showFreightAllowanceControls"
    :id="id"
    class="order-entry-freight-allowance-section"
    :header="t('orders.financials.financials')"
  >
    <Card>
      <template #content>
        <WrappedSwitch
          id="freight-allowance-amount-enabled"
          v-model="freightAllowance.enabled"
          :label="t('orders.freightAllowance.freightAllowance')"
        />
        <InputWrapper
          v-if="freightAllowance.enabled"
          v-slot="{ inputId, errors }"
          input-id="freight-allowance-amount"
          :label="t('orders.freightAllowance.freightAllowanceAmount')"
          optional
        >
          <InputMoney
            :id="inputId"
            v-model="freightAllowance.amount"
            :class="{ 'p-invalid': errors.length }"
            nullable
            :min="0"
            :placeholder="t('orders.freightAllowance.freightAllowanceAmount')"
          />
          <RygenButton
            v-if="
              freightAllowance?.autoCalc?.suggestedAmount &&
              !MoneyUtil.equals(
                freightAllowance.autoCalc.suggestedAmount,
                freightAllowance.amount,
              )
            "
            class="freight-allowance suggestion-button text-left"
            mode="link"
            :label="
              t('common.useSuggestedParam', {
                param: renderMoney(
                  freightAllowance.autoCalc.suggestedAmount.amount,
                  {
                    currencyCode:
                      freightAllowance.autoCalc.suggestedAmount.currency,
                  },
                ),
              })
            "
            @click="useSuggestedFreightAllowance"
          />
          <LoadingIndicator
            v-else-if="
              freightAllowance.autoCalc?.status === 'AUTO_CALC_PENDING'
            "
            loading
          />
        </InputWrapper>

        <InputWrapper
          v-if="freightAllowance.enabled"
          v-slot="{ ariaProps, inputId }"
          input-id="freight-allowance-currency"
          :label="t('currency.currency')"
          optional
        >
          <CurrencyDropdown
            :id="inputId"
            :model-value="freightAllowance.amount?.currency"
            v-bind="ariaProps"
            @update:model-value="onCurrencyUpdate"
          />
        </InputWrapper>
      </template>
    </Card>
  </Section>
</template>

<script setup lang="ts">
import axios from 'axios';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { computed, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Currency, DomainTree, Money } from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  DefaultOrderData,
  FreightAllowance,
  FreightAllowanceAutoCalc,
  Order,
  Shipment,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser.ts';
import MoneyUtil from '@/util/MoneyUtil.ts';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CurrencyDropdown from '@rygen/rygen-ui/components/form/CurrencyDropdown.vue';
import InputMoney from '@rygen/rygen-ui/components/form/InputMoney.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Card from 'primevue/card';

const props = defineProps<{
  id: string;
  modelValue?: FreightAllowance;
  entity: Order | Shipment;
  selectedClient: DomainTree | undefined;
}>();
const { modelValue, entity, selectedClient } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: FreightAllowance): void;
}>();

const { t } = useI18n();
const { selectedDomainId } = useCurrentUserStore();
const toast = useToast();

const freightAllowance = ref(modelValue.value);
const orderDefaults = ref<DefaultOrderData>(new DefaultOrderData());

const showFreightAllowanceControls: Ref<boolean> = computed(() => {
  return (
    authorization.hasAuthority('shipment', 'updateFreightAllowance') &&
    // only allow user to modify freight allowance if is enabled by default
    orderDefaults.value.freightAllowanceEnabled
  );
});

const updateFreightAllowanceAutoCalc = async () => {
  if (!freightAllowance.value?.enabled) {
    return;
  }

  const shouldUpdateFreightAllowance =
    !freightAllowance.value.amount?.amount ||
    isEqual(
      freightAllowance.value.amount,
      freightAllowance.value.autoCalc?.suggestedAmount,
    );

  // loading indicator
  freightAllowance.value.autoCalc = new FreightAllowanceAutoCalc({
    status: 'AUTO_CALC_PENDING',
  });

  try {
    if (entity.value instanceof Order) {
      freightAllowance.value.autoCalc =
        await api.orderFreightAllowance.getSuggestedFreightAllowance(
          entity.value,
        );
    } else {
      freightAllowance.value.autoCalc =
        await api.shipmentFreightAllowance.getSuggestedFreightAllowance(
          entity.value,
        );
    }

    if (shouldUpdateFreightAllowance) {
      useSuggestedFreightAllowance();
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error getting suggested freight allowance amount', e);
    freightAllowance.value.autoCalc = new FreightAllowanceAutoCalc({
      status: 'AUTO_CALC_FAILED',
    });
  }
};

const updateFreightAllowanceAutoCalcDebounced = debounce(
  updateFreightAllowanceAutoCalc,
  500,
);

const useSuggestedFreightAllowance = () => {
  if (freightAllowance.value?.autoCalc?.suggestedAmount) {
    freightAllowance.value.amount =
      freightAllowance.value.autoCalc.suggestedAmount;
  }
};

const onCurrencyUpdate = (newVal: string) => {
  if (!freightAllowance.value?.enabled) {
    return;
  }

  if (!freightAllowance.value.amount) {
    freightAllowance.value.amount = new Money();
  }

  freightAllowance.value.amount.currency = newVal as Currency;
};

const getDefaultOrderData = async () => {
  try {
    orderDefaults.value = await api.client.getDefaultOrderData();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(e);
    toast.addError({
      error: e,
      detail: t('errors.loadingParam', {
        param: t('orders.defaultOrderData'),
      }),
    });
  }
};

watch(
  modelValue,
  (newVal) => {
    if (!isEqual(newVal, freightAllowance.value)) {
      freightAllowance.value = new FreightAllowance(newVal);
    }
  },
  { immediate: true, deep: true },
);

watch(
  freightAllowance,
  (newVal) => {
    if (!isEqual(newVal, modelValue.value)) {
      // Create a new FreightAllowance object with sanitized amount
      let amount: Money | undefined;
      if (newVal?.amount?.amount === undefined) {
        amount = undefined;
      } else if (newVal.amount.amount < 0) {
        amount = Money.zero(newVal.amount.currency);
      } else {
        amount = newVal.amount;
      }
      const sanitizedFreightAllowance = new FreightAllowance({
        ...newVal,
        amount,
      });

      emit('update:model-value', sanitizedFreightAllowance);
    }
  },
  { deep: true },
);

watch(
  () => freightAllowance.value?.enabled,
  (newVal) => {
    if (freightAllowance.value && !newVal) {
      freightAllowance.value.amount = undefined;
    }
  },
  { immediate: true },
);

// Without this `computed`, the watcher's newVal & oldVal child object instances are identical.
// That is to say, if the user modifies e.g. `origin.location.state`,
// then `newVal.origin === oldVal.origin` will be true.
// Boooo!
const entityForAutoCalcWatcher = computed(() => {
  if (entity.value instanceof Order) {
    return new Order(entity.value);
  } else {
    return new Shipment(entity.value);
  }
});

watch(
  entityForAutoCalcWatcher,
  (newVal: Order | Shipment, oldVal: Order | Shipment) => {
    // compare the entire order _except_ the freight allowance amount
    // when the order changes, calculate the freight allowance amount
    if (
      !isEqual(
        { ...newVal, freightAllowance: undefined },
        { ...oldVal, freightAllowance: undefined },
      )
    ) {
      updateFreightAllowanceAutoCalcDebounced();
    }
  },
  { deep: true },
);

watch(
  selectedClient,
  (newVal) => {
    if (
      newVal &&
      selectedDomainId.value &&
      authorization.hasAuthority('client', 'read')
    ) {
      getDefaultOrderData();
    }
  },
  { immediate: true },
);
</script>

<style>
.order-entry-freight-allowance-section {
  .p-card {
    .p-card-content {
      display: flex;
      gap: var(--gap-1);
    }
  }
}
</style>
